import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, MessageCircle } from "lucide-react";
import { SERVICES, CONTACT } from "@/content/site";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Calculator } from "@/components/Calculator";
import { Fixace } from "@/components/Fixace";
import { PovinneRuceni } from "@/components/PovinneRuceni";

// Interaktivní nástroje patří ke konkrétním službám, ne na homepage
const SERVICE_TOOLS = {
  hypoteky: [Calculator, Fixace],
  refinancovani: [Calculator, Fixace],
  uvery: [Calculator, Fixace],
  vozidla: [PovinneRuceni],
};

const ListBlock = ({ title, items, testId }) => (
  <section className="mt-12" data-testid={testId}>
    <h2 className="font-display text-2xl md:text-3xl text-[#252525]">{title}</h2>
    <ul className="mt-6 space-y-4">
      {items.map((item, i) => (
        <li key={i} className="flex gap-4">
          <Check className="h-5 w-5 text-[#B69A58] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
          <p className="text-base text-[#4A4A4A] leading-relaxed">{item}</p>
        </li>
      ))}
    </ul>
  </section>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.id === slug);

  useEffect(() => {
    if (service) {
      document.title = `${service.title} | Daniel Pekař`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", service.text);
    }
    return () => {
      document.title = "Daniel Pekař – finanční poradce Ústí nad Labem";
    };
  }, [service]);

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {!service ? (
            <div data-testid="service-not-found">
              <h1 className="font-display text-4xl text-[#252525]">Tuto stránku jsem nenašel</h1>
              <p className="text-base text-[#4A4A4A] leading-relaxed mt-6">
                Oblast pojištění, kterou hledáte, neexistuje. Podívejte se na přehled toho, s čím vám mohu pomoci.
              </p>
              <Link
                to="/#pojisteni"
                data-testid="service-back-overview"
                className="inline-flex items-center gap-2 mt-8 text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Přehled oblastí pojištění
              </Link>
            </div>
          ) : (
            <article data-testid="service-detail">
              <Link
                to="/#pojisteni"
                data-testid="service-back-link"
                className="inline-flex items-center gap-2 text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Všechny oblasti pojištění
              </Link>

              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mt-10 items-start">
                <div>
                  <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
                    <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
                    Pojištění
                  </p>
                  <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.12] text-[#252525] mt-6">
                    {service.title}
                  </h1>
                  <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-8" data-testid="service-intro">
                    {service.intro}
                  </p>
                </div>

                {service.image && (
                  <figure className="relative" data-testid="service-image">
                    <div
                      className="absolute -bottom-4 -right-4 w-full h-full border border-[#B69A58]/40 pointer-events-none"
                      aria-hidden="true"
                    />
                    <div className="relative border border-[#E5E5E5] bg-white p-3">
                      <div className="absolute top-0 left-8 w-16 h-[1px] bg-[#B69A58]" aria-hidden="true" />
                      <img
                        src={service.image}
                        alt={service.imageAlt}
                        width={1200}
                        height={685}
                        loading="lazy"
                        className="w-full h-auto block"
                      />
                    </div>
                  </figure>
                )}
              </div>

              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
                <ListBlock title="Kdy se to hodí" items={service.when} testId="service-when" />
                <ListBlock title="Co společně vyřešíme" items={service.solve} testId="service-solve" />
              </div>

              <div className="w-full h-[1px] bg-[#F0F0F0] my-14" aria-hidden="true" />

              <section data-testid="service-cta" className="border border-[#F0F0F0] p-8 md:p-10">
                <h2 className="font-display text-2xl md:text-3xl text-[#252525]">Chcete to probrat?</h2>
                <p className="text-base text-[#4A4A4A] leading-relaxed mt-4">
                  Napište mi nebo zavolejte — domluvíme nezávaznou konzultaci, osobně v Ústí nad Labem
                  nebo online po celé ČR.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <Link
                    to="/#termin"
                    data-testid="service-cta-consultation"
                    className="inline-flex items-center justify-center border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
                  >
                    Domluvit konzultaci
                  </Link>
                  <a
                    href={CONTACT.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-cta-whatsapp"
                    className="inline-flex items-center justify-center gap-2 border border-[#E5E5E5] text-[#252525] hover:border-[#252525] transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
                  >
                    <MessageCircle className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                    Napsat na WhatsApp
                  </a>
                </div>
              </section>
            </article>
          )}
        </div>

        {service && SERVICE_TOOLS[service.id] && (
          <div data-testid="service-tools">
            {SERVICE_TOOLS[service.id].map((Tool, i) => (
              <Tool key={i} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
